import { useState } from 'react';
import { Mail, Loader2, Check } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Analytics } from '@/lib/analytics';

interface NewsletterFormProps {
  source: 'landing' | 'central-conteudo' | 'conteudo-detalhe';
  variant?: 'footer' | 'inline';
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const NewsletterForm = ({ source, variant = 'footer' }: NewsletterFormProps) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!EMAIL_REGEX.test(trimmed)) {
      toast.error('Informe um e-mail válido');
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('newsletter-subscribe', {
        body: { email: trimmed, source },
      });
      if (error) throw error;
      if (data?.already_subscribed) {
        toast.success('Você já está na nossa lista - obrigada!');
      } else {
        toast.success('Inscrição confirmada! Em breve novidades no seu e-mail.');
      }
      Analytics.newsletterSubscribed(source);
      setSuccess(true);
      setEmail('');
    } catch (err) {
      console.error(err);
      toast.error('Não foi possível inscrever agora. Tente novamente em instantes.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="flex items-center gap-2 text-sm text-primary font-body">
        <Check className="h-4 w-4" />
        <span>Inscrição confirmada - obrigada!</span>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-2">
      <div className={variant === 'footer' ? 'flex flex-col sm:flex-row gap-2' : 'flex flex-col sm:flex-row gap-2'}>
        <div className="relative flex-1">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <Input
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder="seu@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
            aria-label="Seu e-mail"
            className="pl-9 h-10 bg-background"
            required
          />
        </div>
        <Button type="submit" disabled={loading} className="font-body shrink-0">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Quero receber'}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground font-body">
        Conteúdos sobre gestão artesanal, dicas e novidades. Sem spam.
      </p>
    </form>
  );
};

export default NewsletterForm;
